import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { format, isToday, isValid, isYesterday } from "date-fns";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useCurrentSize } from "../CurrentSize";
import { AppContext } from "../Contexts";
import ListViewSkeleton from "../main/list-view/ListViewSkeleton";
import HoverActionsMenu from "../components/HoverActionsMenu";
import ListViewEmptyState from "../main/list-view/ListViewEmptyState";
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  ChartArea,
  LayoutDashboard,
  Plus,
} from "lucide-react";
import { localDateTimeInTimeZone } from "../ai-content/helpers";
import DashboardAction from "../actions/DashboardAction";
import { deleteDashboard, saveDashboard } from "../api/InsightApi";
import LayoutAction from "../actions/LayoutAction";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";

type DashboardData = {
  id: string;
  title: string;
  reports: any[];
  createdAt: number;
  createdBy: any;
};

const formatWithTime = (date: Date) => {
  if (!isValid(date)) return `-`;
  if (isToday(date)) return `Today`;
  if (isYesterday(date)) return `Yesterday`;
  return format(date, "dd MMM yyyy");
};

const DashboardItem = ({
  projectId,
  dashboard,
  onOpen,
  onDelete,
}: {
  projectId: string;
  dashboard: DashboardData;
  onOpen: (dashboard: DashboardData) => void;
  onDelete: (dashboard: DashboardData) => void;
}) => {
  const navigate = useNavigate();

  return (
    <div className="relative group flex items-center justify-start gap-2 py-2 hover:bg-gray-50 transition-colors rounded-md">
      {/* Title */}
      <button
        type="button"
        onClick={() => onOpen(dashboard)}
        className="flex-2 ml-2 flex items-center justify-start gap-2 text-sm font-medium text-gray-900 truncate cursor-pointer"
        title={dashboard.title}
      >
        <ChartArea className="w-4 h-4 text-gray-600" />
        {dashboard.title}
      </button>

      {/* Reports */}
      <div className="flex-4 flex items-center gap-2">
        {dashboard.reports?.map((item) => {
          const report = fromJS(item || {});
          return (
            <div
              key={report.get("widgetId")}
              className="p-1 text-xs bg-gray-100 rounded-md cursor-pointer"
              onClick={() => {
                navigate(
                  `/workspaces/${projectId}/reports/dashboard/${dashboard.id}/${report.getIn(["report", "id"])}?key=Reports&reportType=${
                    report.getIn(["report", "type"]) || ""
                  }&reportChartType=${report.getIn(["report", "data", "chartType"]) || ""}&reportObjectType=${
                    report.getIn(["report", "data", "objectType"]) || "SALE"
                  }&reportViewMode=true`
                );
              }}
            >
              {report.getIn(["data", "title"])}
            </div>
          );
        })}
      </div>

      {/* Created at */}
      <div className="flex-1">{formatWithTime(new Date(dashboard.createdAt))}</div>

      {/* Hover Actions Menu */}
      <HoverActionsMenu onOpen={() => onOpen(dashboard)} onDelete={() => onDelete(dashboard)} />
    </div>
  );
};

const DashboardList = () => {
  const currentSize = useCurrentSize();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const appContext = useContext<any>(AppContext);

  const [size, setSize] = useState(currentSize);
  const [sortBy, setSortBy] = useState<"ASC" | "DESC">("ASC");
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const pending = useSelector((state: any) => state.dashboard.get("pending"));
  const dashboards = useSelector((state: any) => state.dashboard.get("dashboards")) || fromJS([]);

  useEffect(() => {
    document.title = t("Report List");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dashboards.size === 1 && dashboards.first().get("items").size === 0) {
      navigate(
        `/workspaces/${projectId}/reports/dashboard/${dashboards.first().get("id")}?key=Reports`
      );
    }
  }, [dashboards]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const minusHeight = appContext.appName == "chrome" ? 0 : 0;
    const minusWidth = appContext.appName == "chrome" ? 56 : 0;
    setSize({
      width: currentSize.width - minusWidth,
      height: currentSize.height - minusHeight,
    });
  }, [currentSize, appContext]);

  const dashboardDatas = useMemo(() => {
    const datas = dashboards.map((item) => {
      const createdAt = item.get("createdAt");
      const utcDate = new Date(createdAt.replace(" ", "T") + "Z");
      const timezone = item.getIn(["createdBy", "tz"]);
      const createdBy = item.get("createdBy")?.toJS() || {};
      const reports = item.get("items")?.toJS() || [];
      return {
        id: item.get("id"),
        title: item.get("name") || "",
        reports: reports,
        createdAt: localDateTimeInTimeZone(utcDate, timezone).getTime(),
        createdBy: createdBy,
      };
    });
    if (sortBy === "ASC") {
      return datas.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    }
    return datas.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [dashboards, sortBy]);

  const handleOpen = (dashboard: DashboardData) => {
    navigate(`/workspaces/${projectId}/reports/dashboard/${dashboard.id}?key=Reports`);
  };

  const handleDelete = (dashboard: DashboardData) => {
    setShowConfirm({
      show: true,
      title: t("Delete dashboard"),
      description: t("Are you sure you want to remove this dashboard?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: dashboard,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      const { id } = showConfirm.data;
      deleteDashboard(projectId, id)
        .then(() => {
          dispatch(DashboardAction.getDashboards(projectId));
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete dashboard"),
              type: "success",
              message: t("Delete dashboard successfully!"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete dashboard"),
              type: "error",
              message: t("Failed to delete dashboard"),
            })
          );
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleAddNewDashboard = () => {
    const newDashboard = {
      projectId: project.get("id"),
      tenantId: project.get("tenantId"),
      name: "New Dashboard",
      type: "DASHBOARD",
      items: [],
    };
    saveDashboard(projectId, newDashboard)
      .then((response) => {
        dispatch(DashboardAction.getDashboards(projectId));
        navigate(`/workspaces/${projectId}/reports/dashboard/${response.id}?key=Reports`);
        dispatch(
          LayoutAction.setNotification({
            title: t("Dashboard"),
            type: "success",
            message: t("Add new dashboard successfully!"),
          })
        );
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("Dashboard"),
            type: "error",
            message: t("Failed to save new dashboard"),
          })
        );
      });
  };

  const renderView = () => {
    if (pending) {
      return (
        <div className="p-4">
          <ListViewSkeleton
            columns={[
              { label: "Dashboard", flex: "flex-2" },
              { label: "Reports", flex: "flex-4" },
              { label: "Created at", flex: "flex-1" },
            ]}
            rows={6}
          />
        </div>
      );
    }
    return (
      <div className="p-4">
        <div className="w-full flex flex-col gap-3">
          {dashboards.size === 0 ? (
            <ListViewEmptyState
              icon={LayoutDashboard}
              title="Dashboards"
              description="No dashboards yet! Create your first dashboard to get started."
              actionLabel="New dashboard"
              onAction={handleAddNewDashboard}
            />
          ) : (
            <>
              <div className="w-full">
                <div className="flex items-center justify-start gap-2 py-2 bg-gray-100">
                  <div className="pl-4 flex-2">{t("Dashboard")}</div>
                  <div className="flex-4">{t("Reports")}</div>
                  <div className="flex-1">{t("Created at")}</div>
                </div>
              </div>
              <div className="w-full">
                {dashboardDatas.map((dashboard) => (
                  <DashboardItem
                    key={dashboard.id}
                    projectId={projectId}
                    dashboard={dashboard}
                    onOpen={handleOpen}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </>
          )}

          <ConfirmDialog
            visible={showConfirm.show}
            title={showConfirm.title}
            description={showConfirm.description}
            alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
            dangerBtn={showConfirm.dangerBtn}
            onSubmit={handleSubmitConfirm}
            onClose={() => {
              setShowConfirm({ show: false, title: "", description: "" });
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="w-full pl-1.5 py-2 flex h-10 bg-white">
        <div className="flex flex-shrink-0 space-x-2">
          <div
            className="rounded-md h-8 border border-gray-300 cursor-pointer"
            onClick={() => setSortBy(sortBy === "ASC" ? "DESC" : "ASC")}
          >
            <div className="h-8 px-2 py-1.5 flex items-center space-x-2 text-sm font-medium">
              {sortBy === "ASC" && <ArrowUpNarrowWide className="h-4 w-4 text-gray-500" />}
              {sortBy === "DESC" && <ArrowDownWideNarrow className="h-4 w-4 text-gray-500" />}
              <div className="text-gray-500">{t("Sorted by")}</div>
              <div>{t("Creation date")}</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-4 items-center pr-6 w-full">
          <button
            onClick={handleAddNewDashboard}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("New dashboard")}
          </button>
        </div>
      </div>
      <div style={{ maxWidth: size.width - 60 }}>{renderView()}</div>
    </div>
  );
};

export default DashboardList;
